<template>
	<v-container class="dashboard-container">
		<v-overlay :value="tutorialActive" :z-index="9998" opacity="0.6"></v-overlay>
		<v-card
			v-if="tutorialActive && currentTutorialInfo"
			class="tutorial-info-card elevation-12"
			:style="tutorialCardPosition"
			>
			<v-card-title class="pb-2 primary white--text">
				<v-icon left color="white" small>fas fa-lightbulb</v-icon>
				<span class="text-subtitle-1 font-weight-bold">{{ currentTutorialInfo.title }}</span>
				<v-spacer></v-spacer>
				<v-btn icon small dark @click="closeTutorial">
				<v-icon small>fas fa-times</v-icon>
				</v-btn>
			</v-card-title>
			<v-card-text class="py-3">
				<p class="mb-0 text-body-2" v-html="currentTutorialInfo.message"></p>
			</v-card-text>
			<v-card-actions class="pt-0 pb-3 px-4 justify-end">
				<v-btn small color="primary" @click="goToNextStep">
					{{ isLastStep ? 'Finish' : 'Next' }}
					<v-icon right small>
						{{ isLastStep ? 'fas fa-check' : 'fas fa-arrow-right' }}
					</v-icon>
				</v-btn>
			</v-card-actions>
		</v-card>
		<v-row class="contentRow">
			<v-col align="center">
				<h1>Onsite Audit Initiator Dashboard</h1>
				<p>Track and manage your audit process from initiation to completion</p>
			</v-col>
		</v-row>
		<v-row class="contentRow">
			<v-col cols="12">
				<div class="steps-wrapper">
					<div 
						class="step-container clickable-step"
						@click="handleWorkflowStepClick('step1')"
						:class="{ 'step-active': activeStep === 'step1' }">
						<div class="step-circle">
							<v-icon size="35" color="trace">fas fa-file-invoice</v-icon>
						</div>
						<div class="step-text">
							<h5>Send Template Request to Auditor</h5>
						</div>
					</div>

					<!-- Arrow 1 -->
					<v-col class="arrow-col">
						<v-icon size="25">fas fa-arrow-right-long</v-icon>
					</v-col>

					<!-- Step 2: Get Template Approval -->
					<div class="step-container clickable-step"
						@click="handleWorkflowStepClick('step2')"
						:class="{ 'step-active': activeStep === 'step2' }">
						<div class="step-circle">
							<v-icon size="35" color="success">fas fa-user-check</v-icon>
						</div>
						<div class="step-text">
							<h5>Get Template Approval from Auditor</h5>
						</div>
					</div>

					<!-- Arrow 2 -->
					<v-col class="arrow-col">
						<v-icon size="25">fas fa-arrow-right-long</v-icon>
					</v-col>

					<!-- Step 3: Send Audit Request to Supplier -->
					<div class="step-container clickable-step" 
						@click="handleWorkflowStepClick('step3')"
						:class="{ 'step-active': activeStep === 'step3' }">
						<div class="step-circle">
							<v-icon size="35" color="error">fas fa-share-from-square</v-icon>
						</div>
						<div class="step-text">
							<h5>Send Onsite-Audit Request to Supplier and Auditor</h5>
						</div>
					</div>

					<!-- Arrow 3 -->
					<v-col class="arrow-col">
						<v-icon size="25">fas fa-arrow-right-long</v-icon>
					</v-col>

					<!-- Step 4: Wait for Filled Audit -->
					<div class="step-container clickable-step"
						@click="handleWorkflowStepClick('step4')"
						:class="{ 'step-active': activeStep === 'step4' }">
						<div class="step-circle">
							<v-icon size="35" color="warning">fas fa-hourglass-half</v-icon>
						</div>
						<div class="step-text">
							<h5>Wait for auditor’s completion and supplier’s improvements.</h5>
						</div>
					</div>

					<!-- Arrow 4 -->
					<v-col class="arrow-col">
						<v-icon size="25">fas fa-arrow-right-long</v-icon>
					</v-col>

					<!-- Step 5: Assess Audit Results -->
					<div class="step-container clickable-step"
						@click="handleWorkflowStepClick('step5')"
						:class="{ 'step-active': activeStep === 'step5' }">
						<div class="step-circle">
							<v-icon size="35" color="primary">fas fa-scale-balanced</v-icon>
						</div>
						<div class="step-text">
							<h5>Assess Onsite-Audit Results by Initiator</h5>
						</div>
					</div>

					<!-- Arrow 5 -->
					<v-col class="arrow-col">
						<v-icon size="25">fas fa-arrow-right-long</v-icon>
					</v-col>

					<!-- Step 6: Process Complete -->
					<div class="step-container clickable-step"
						@click="handleWorkflowStepClick('step6')"
						:class="{ 'step-active': activeStep === 'step6' }">
						<div class="step-circle">
							<v-icon size="35" color="success">fas fa-square-check</v-icon>
						</div>
						<div class="step-text">
							<h5>Process Completed</h5>
						</div>
					</div>
				</div>
			</v-col>
		</v-row>
		<v-row class="contentRow">
			<v-col cols="12" class="d-flex justify-end">
				<v-btn 
					ref="createBtn"                                          
					class="mt-2" 
					dark 
					v-if="$accessToOperation('AR','C')" 
					to="/auditonsite/onsitesender/newonsite" 
					color="orangeButton"
					:class="{ 'tutorial-highlight': activeStep === 'step1' }" 
					>
				<v-icon left small>fas fa-plus</v-icon>
					Onsite Audit Request
				</v-btn>
			</v-col>
			<v-col>
				<v-card 
					ref="pendingSection"                                       
					elevation="0" 
					outlined
					:class="{ 
						'tutorial-highlight': ['step2', 'step3', 'step4'].includes(activeStep),
						'tutorial-grayed': tutorialActive && !['step2', 'step3', 'step4'].includes(activeStep)
					}">
					<v-card-title class="text-center justify-left py-6">
						<h3 class="darkColor--text">Pending Requests</h3>
					</v-card-title>
					<v-row class="ma-2">
						<v-col cols="3" class="pr-0">
							<v-text-field
								class="ml-0"
								dense
								background-color="#FAFAFA"
								prepend-inner-icon="fas fa-search"
								label="Template name"
								v-model="templateName"
								outlined
							>
							</v-text-field>
						</v-col>
						<v-col cols="3" class="pl-0 pr-0">
							<v-text-field
								class="ml-0"
								dense
								background-color="#FAFAFA"
								prepend-inner-icon="fas fa-search"
								label="Onsite-Auditor"
								v-model="auditorName"
								outlined
							>
							</v-text-field>
						</v-col>
						<v-col cols="auto">
							<v-btn :disabled="!templateName && !auditorName" class="mr-1" elevation="0" medium color="white" @click="clearPendingSearch()">Clear</v-btn>
							<v-btn class="ml-0" medium color="primary" @click="searchPendingRequests()"><v-icon>fas fa-search</v-icon>Search</v-btn>
						</v-col>
					</v-row>
					<v-data-table
						:loading="loading"
						:headers="headersPendingRequests"
						:items="pagedCombinedRequests"
						:options.sync="pendingtemplates_options"
						:server-items-length="filteredCombinedRequests.length"
						:items-per-page="pendingtemplates_options.itemsPerPage"
						class="light table"
						:footer-props="{ 'items-per-page-options': [5, 10, 15] }"
					>
						<template v-slot:item.auditorName="{ item }">
							<span v-if="item.auditorName">
								<v-tooltip top>
									<template v-slot:activator="{ props, on }">
										<span
											v-bind="props"
											v-on="on"
										>
											{{ item.auditorName }}
										</span>
									</template>
									<span>{{ item.auditorType == 1 ? 'Internal Onsite-Auditor' : 'External Onsite-Auditor' }}</span>
								</v-tooltip>
							</span>
							<span v-else class="error--text font-italic">Auditor need to register</span>
						</template>
						<template v-slot:item.status="{ item }">
							<v-tooltip top v-if="item.type === 'template' && item.templateApproval === 0">
								<template v-slot:activator="{ props,on }">
									<span
										v-bind="props"
										v-on="on"
										@click="showRequestDialog(item)"
										style="cursor: pointer"
										class="trace--text d-inline-flex align-center"
									>
										Pending Template Approval...
									</span>
								</template>
								<span>Auditor needs to approve the template</span>
							</v-tooltip>
							<v-tooltip top v-else-if="item.type === 'template' && item.templateApproval === 1">
								<template v-slot:activator="{ props,on }">
									<span
										v-bind="props"
										v-on="on"
										@click="continueRequest(item)"
										style="cursor: pointer"
										class="error--text d-inline-flex align-center"
									>
										Continue Request
										<v-icon right small color="error">fas fa-arrow-right</v-icon>
									</span>
								</template>
								<span>Complete your template request</span>
							</v-tooltip>
							<v-tooltip top v-else-if="item.type === 'audit'">
								<template v-slot:activator="{ props,on }">
									<span
										v-bind="props"
										v-on="on"
										@click="showRequestDialog(item)"
										style="cursor: pointer"
										class="orange--text d-inline-flex align-center"
									>
										Pending Audit Submitting...
									</span>
								</template>
								<span>Auditor needs to complete the Onsite-Audit</span>
							</v-tooltip>
						</template>
					</v-data-table>
				</v-card>
			</v-col>
		</v-row>
		<v-row class="contentRow">
			<v-col>
				<v-card 
					ref="assessSection"                                         
					elevation="0" 
					outlined
					:class="{ 
						'tutorial-highlight': ['step4', 'step5', 'step6'].includes(activeStep),
						'tutorial-grayed': tutorialActive && !['step4', 'step5', 'step6'].includes(activeStep)
					}">
					<v-card-title class="text-center justify-left py-6">
						<h3 class="darkColor--text">Audits to Assess</h3>
					</v-card-title>
					<v-row class="contentRow">
						<v-col>
							<v-card elevation="0" outlined>
								<v-row align="center" justify="space-between" class="pa-4" style="background-color: #f9f9f9">
									<v-col cols="8" class="pa-0">
										<v-simple-table style="background: #e8e8e8; border-radius: 8px;">
											<template v-slot:default>
												<tbody>
													<tr>
														<td>Improvements/Nonconformities sent since last year</td>
														<td class="text-right font-weight-medium">{{ sentCount }}</td>
													</tr>
													<tr>
														<td>Completed Improvements/Nonconformities since last year</td>
														<td class="text-right font-weight-medium">{{ completedCount }}</td>
													</tr>
												</tbody>
											</template>
										</v-simple-table>
									</v-col>
									<v-col cols="4" class="text-center">
										<v-progress-circular
										:size="100"
										:width="8"
										:value="completionPercent"
										color="green"
										>
										<span class="text-h6 font-weight-medium">{{ completionPercent }}%</span>
										</v-progress-circular>
									</v-col>
								</v-row>
							</v-card>
						</v-col>
					</v-row>
					<v-row class="ma-2">
						<v-col cols="3" class="pr-0">
							<v-text-field
								class="ml-0"
								dense
								background-color="#FAFAFA"
								prepend-inner-icon="fas fa-search"
								label="Template name"
								v-model="assessTemplateName"
								outlined
							>
							</v-text-field>
						</v-col>
						<v-col cols="3" class="pl-0 pr-0">
							<v-text-field
								class="ml-0"
								dense
								background-color="#FAFAFA"
								prepend-inner-icon="fas fa-search"
								label="Supplier"
								v-model="supplierName"
								outlined
							>
							</v-text-field>
						</v-col>
						<v-col cols="3" class="pl-0">
							<v-text-field
								class="ml-0"
								dense
								background-color="#FAFAFA"
								prepend-inner-icon="fas fa-search"
								label="Onsite-Auditor"
								v-model="assessAuditorName"
								outlined
							>
							</v-text-field>
						</v-col>
						<v-col cols="auto">
							<v-btn :disabled="!assessTemplateName && !supplierName && !assessAuditorName" class="mr-1" elevation="0" medium color="white" @click="clearAssessSearch()">Clear</v-btn>
							<v-btn class="ml-0" medium color="primary" @click="searchAuditsToAssess()"><v-icon>fas fa-search</v-icon>Search</v-btn>
						</v-col>
					</v-row>
					<v-data-table
						:loading="loadingAssess"
						:headers="headersAuditsToAssess"
						:items="pagedAuditsToAssess"
						:options.sync="auditstoassess_options"
						:server-items-length="filteredAuditsToAssess.length"
						:items-per-page="auditstoassess_options.itemsPerPage"
						class="light table"
						:footer-props="{ 'items-per-page-options': [5, 10, 15] }"
					>
						<template v-slot:item.auditorName="{ item }">
							<v-tooltip top>
								<template v-slot:activator="{ props, on }">
									<span
										v-bind="props"
										v-on="on"
									>
										{{ item.auditorName }}
									</span>
								</template>
								<span v-if="item.auditorType === 1">Internal Onsite-Auditor</span>
								<span v-if="item.auditorType === 2">External Onsite-Auditor's company name</span>
							</v-tooltip>
						</template>
						<template v-slot:item.onsiteDate="{ item }">
							<span v-if="item.visitDays && item.onsiteDate">
								{{ item.onsiteDate }}
								<v-tooltip top>
									<template v-slot:activator="{ on, attrs }">
										<span
											v-bind="attrs"
											v-on="on"
											class="grey--text text--darken-1"
										>
											({{ item.visitDays }} days)
										</span>
									</template>
									<span>Number of visit days</span>
								</v-tooltip>
							</span>
							<span v-else>
								{{ item.onsiteDate }}
							</span>
						</template>
						<template v-slot:item.status="{ item }">
							<v-tooltip top v-if="item.type === 'completed' && hasIncompleteImprovements(item.id)">
								<template v-slot:activator="{ on, attrs }">
									<span
										v-bind="attrs"
										v-on="on"
										@click="navigateToAudit(item)"
										style="cursor: pointer"
										class="warning--text d-inline-flex align-center"
									>
										Improvements Pending...
									</span>
								</template>
								<span>Supplier need to complete all nonconformities and improvements</span>
							</v-tooltip>
							<v-tooltip top v-else-if="item.type === 'completed'">
								<template v-slot:activator="{ on, attrs }">
									<span
										v-bind="attrs"
										v-on="on"
										@click="navigateToAudit(item)"
										style="cursor: pointer"
										class="primary--text d-inline-flex align-center"
									>
										Awaiting Assess
										<v-icon right small color="primary">fas fa-arrow-right</v-icon>
									</span>
								</template>
								<span>You need to assess the Onsite-Audit</span>
							</v-tooltip>
							<!-- For already assessed/completed audits -->
							<v-tooltip top v-else-if="item.type === 'assessed'">
								<template v-slot:activator="{ on, attrs }">
									<span
										v-bind="attrs"
										v-on="on"
										@click="navigateToAudit(item)"
										style="cursor: pointer"
										class="success--text d-inline-flex align-center"
									>
										Completed 
										<v-icon class="ml-2" color="success">fas fa-circle-check</v-icon>
									</span>
								</template>
								<span>Onsite-Audit has been completed</span>
							</v-tooltip>
						</template>
					</v-data-table>
				</v-card>
			</v-col>
		</v-row>
		<v-dialog 
			v-model="dialogRequestView"
			max-width="1200px"
			height="auto"
			scrollable
			align = "center">
			<v-card>
				<v-card-text class="background-color: white" v-if="previewTemplateID!=null">		
					<template-preview
						:previewID="previewTemplateID"
						:preview="false"
						:submitterName="submitterName"
					/>
				</v-card-text>
				<v-card-actions>
					<v-btn 
						color="primary" 
						text 
						@click="dialogRequestView = false; submitterName = null; previewTemplateID=null" 
						outlined
					>Close</v-btn
					>
				</v-card-actions>
			</v-card>
		</v-dialog>  
	</v-container>
</template>
<script>
import AuditsAPI from "../../api/audit/audit"
import TemplateRequestAPI from "../../api/audit/template"
import AuditRequestsAPI from "../../api/audit/request"
import TemplatePreview from "../auditonsite/OnsiteTemplatePreview.vue"
import AuditAPI from "../../api/audit/audit"
import { mapGetters,mapActions,mapState } from "vuex"
/* import LoadingState from "../../consts/LoadingState" */
export default {
	name: "Initiator-Dashboard",
	components: {
		"template-preview" : TemplatePreview
	},
	data() {
		return {
			headersPendingRequests: [
				{ text: "Template Name", value: "templateName", sortable: false, class: "darkColor--text" },
				{ text: "Onsite-Auditor", value: "auditorName", sortable: false, class: "darkColor--text" },
				{ text: "Timestamp", value: "timestamp", sortable: false, class: "darkColor--text" },
				{ text: "Status", value: "status", sortable: false, class: "darkColor--text" }
			],
			headersAuditsToAssess: [
				{ text: "Template Name", value: "templateName", sortable: false, class: "darkColor--text" },
				{ text: "Supplier", value: "sender", sortable: false, class: "darkColor--text" },
				{ text: "Onsite-Auditor", value: "auditorName", sortable: false, class: "darkColor--text" },
				{ text: "Timestamp", value: "timestamp", sortable: false, class: "darkColor--text" },
				{ text: "Onsite Date", value: "onsiteDate", sortable: false, class: "darkColor--text" },
				{ text: "Status", value: "status", sortable: false, class: "darkColor--text" },
			],
			pendingtemplates_options: {
				page: 1,
				itemsPerPage: 5,
				templateName: null,
				auditorName: null,
				sort: null
			},
			auditstoassess_options: {
				page: 1,
				itemsPerPage: 5,
				templateName: null,
				senderName: null,
				supplierName: null,
				sort: null
			},
			pending: "pendingtemplate",
			templateApprovals: [],
			allTemplateApprovals: [],
			pendingaudits: [],
			allPendingAudits: [],
			allCombinedRequests: [],
			completedAudits: [],
			assessedAudits: [],
			allAuditsToAssess: [],
			previewTemplateID: null,
			dialogRequestView: false,
			submitterName: null,
			loading: false,
			loadingAssess: false,
			filteredCombinedRequests: [],
			filteredAuditsToAssess: [],
			templateName: null,
			auditorName: null,
			assessTemplateName: null,
			supplierName: null,
			assessAuditorName: null,
			auditImprovements: {},
			sentCount: 0,
			completedCount: 0,
			tutorialActive: false,
			activeStep: null,
			tutorialSteps: {
				step1: {
					title: "Step 1: Send Template Request to Auditor",
					message: "You can send your template request from here. Click the '<span class='orange--text'>+ Onsite Audit Request</span>' button to select or create an onsite template and send it to your auditor for approval.",
					targetRef: "createBtn"
				},
				step2: {
					title: "Step 2: Get Template Approval from Auditor",
					message: "If you see '<span class='trace--text'>Pending Template Approval…</span>', it means you must wait for the auditor to approve the template. Once approved, you can continue and send the audit request to the supplier.",
					targetRef: "pendingSection"
				},
				step3: { 
					title: "Step 3: Send Onsite-Audit Request to Supplier and Auditor",
					message: "After the template is approved, use the '<span class='error--text'>Continue Request</span>' option in the Pending Requests table to send the audit to the supplier and auditor.",
					targetRef: "pendingSection"
				},
				step4: { 
					title: "Step 4: Wait for Audit Completion and Supplier Improvements",
					message: "'<span class='orange--text'>Pending Audit Submitting…</span>' means the auditor has not submitted the audit yet (shown in Pending Requests). '<span class='orange--text'>Improvements Pending…</span>' means the supplier still needs to complete improvements (shown in Audits to Assess).",
					targetRef: "pendingSection"
				},
				step5: {
					title: "Step 5: Assess Onsite-Audit Results",
					message: "Review completed audits by clicking '<span class='primary--text'>Awaiting Assess</span>' and verify that all improvements and nonconformities have been addressed.",
					targetRef: "assessSection"
				},
				step6: { 
					title: "Step 6: Process Completed",
					message: "Once assessed, audits are marked as '<span class='success--text'>Completed</span>'.",
					targetRef: "assessSection"
				}

			}
		}
	},
	mounted(){
		this.loadAllRequests()
		this.loadAuditsToAssess()
	},
	computed: {
		...mapGetters({
			isLoggedIn: "user/isLoggedIn",
			getUser: "user/getUser",
			isApproved: "audit/template/isApproved",
		}),
		...mapState("audit/template", {
			approval: state => state.approval
		}),
		clientName() {
			return this.getUser.clientName
		},
		pagedCombinedRequests() {
			const start = (this.pendingtemplates_options.page - 1) * this.pendingtemplates_options.itemsPerPage
			const end = start + this.pendingtemplates_options.itemsPerPage
			return this.filteredCombinedRequests.slice(start, end)
		},
		pagedAuditsToAssess() {
			const start = (this.auditstoassess_options.page - 1) * this.auditstoassess_options.itemsPerPage
			const end = start + this.auditstoassess_options.itemsPerPage
			return this.filteredAuditsToAssess.slice(start, end)
		},
		completionPercent() {
			if (this.sentCount === 0) return 0
			return Math.round((this.completedCount / this.sentCount) * 100)
		},
		currentTutorialInfo() {             
			if (!this.activeStep) return null
			return this.tutorialSteps[this.activeStep]
		},
		tutorialCardPosition() {            
			if (!this.activeStep || !this.currentTutorialInfo) return {}
			
			const targetRef = this.currentTutorialInfo.targetRef
			if (!targetRef || !this.$refs[targetRef]) {
				return {
					position: "fixed",
					top: "20%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					zIndex: 10001,
					maxWidth: "600px"
				}
			}
			this.$nextTick(() => {
				const el = this.$refs[targetRef]
				const element = el.$el || el
				if (element) {
					const rect = element.getBoundingClientRect()
					const cardWidth = 600
					const cardHeight = 160
					
					let top = rect.bottom + 20
					let left = rect.left + (rect.width / 2) - (cardWidth / 2)
					
					// Adjust if card goes off screen
					if (top + cardHeight > window.innerHeight) {
						top = rect.top - cardHeight - 20
					}
					if (left < 20) left = 20
					if (left + cardWidth > window.innerWidth) {
						left = window.innerWidth - cardWidth - 20
					}
					
					return {
						position: "fixed",
						top: `${top}px`,
						left: `${left}px`,
						zIndex: 10001,
						maxWidth: "600px"
					}
				}
			})
			
			return {
				position: "fixed",
				top: "20%",
				left: "50%",
				transform: "translate(-50%, -50%)",
				zIndex: 10001,
				maxWidth: "600px"
			}
		},
		stepOrder() {
			return ["step1", "step2", "step3", "step4", "step5", "step6"]
		},
		isLastStep() {
			return this.activeStep === "step6"
		}
	},
	methods: {
		...mapActions("audit/template", ["approveTemplate"]),
		continueRequest(item){
			const templateID = item?.templateID
			const auditorName = item?.auditorName || ""

			if (!templateID) {
				throw new Error("Template ID not found.")
			}
			
			if (!this.isApproved) {
				this.approveTemplate({ id: templateID, approval: true })
			}

			setTimeout(() => {
				this.$router.push({
					path: "/auditonsite/onsitesender/newonsite",
					query: {
						templateID,
						auditorName,
						step: "3"
					}
				})
			}, 1000)
		},
		navigateToAudit(item) {
			this.$router.push(`/auditonsite/onsiteaudits/${item.id}`)
		},
		loadAllRequests() {
			this.loading = true
			Promise.all([
				TemplateRequestAPI.getOnsiteTemplateRequest(this.clientName),
				AuditRequestsAPI.onsiteList({ type: "outgoing" })
			])
				.then(([templateResult, auditResult]) => {
					
					this.allTemplateApprovals = templateResult.map(entry => ({
						type: "template",
						templateID: entry.TemplateID,
						templateName: entry.TemplateName,
						auditorName: entry.AuditorName,
						timestamp: this.$moment(entry.Timestamp).format("DD/MM/YYYY HH:mm:ss"),
						timestampRaw: entry.Timestamp,
						templateApproval: entry.TemplateApproval,
						auditorType: entry.AuditorType
					}))


					this.allPendingAudits = auditResult.items.map(entry => ({
						type: "audit",
						id: entry.requestID,
						templateID: entry.templateID,
						templateName: entry.templateName,
						auditorName: entry.auditorName,
						auditorType: entry.auditorType,
						timestamp: this.$moment(entry.timestamp).format("DD/MM/YYYY HH:mm:ss"),
						timestampRaw: entry.timestamp,
						sender: entry.recipientName || entry.invitationCodeName
					}))


					this.allCombinedRequests = [
						...this.allTemplateApprovals,
						...this.allPendingAudits
					].sort((a, b) => new Date(b.timestampRaw) - new Date(a.timestampRaw))

					this.filteredCombinedRequests = [...this.allCombinedRequests]

					this.loading = false
				})
				.catch(err => {
					console.error("Error loading requests:", err)
					this.loading = false
				})
		},
		shouldShowAudit(fromDate, toDate) {
			// Use fromDate if available, otherwise use toDate
			const dateToCheck = fromDate || toDate
			
			if (!dateToCheck) return false
			
			const checkDate = this.$moment(dateToCheck)
			const today = this.$moment()
			
			// Get the year from the date we're checking
			const checkYear = checkDate.year()
			
			// Calculate the end of next year from the check date's year
			const nextYearEnd = this.$moment(`${checkYear + 1}-12-31`, "YYYY-MM-DD").endOf("day")
			
			// Show if today hasn't passed the end of next year
			return today.isSameOrBefore(nextYearEnd, "day")
		},
		loadAuditsToAssess() {
			this.loadingAssess = true
			
			Promise.all([
				AuditsAPI.onsiteList({type: "incoming", status: "0", limit: 200, offset: 0}), 		//assessed audits
				AuditsAPI.onsiteList({type: "incoming", notStatus: "0", limit: 200, offset: 0}) 	//not assessed audits
			])
				.then(([completedResult, assessedResult]) => {
					// Process completed audits and filter by date
					this.completedAudits = completedResult.items
						.filter(entry => this.shouldShowAudit(entry.fromDate, entry.toDate))
						.map(entry => {
							let onsiteDate = null
							if(entry.fromDate && entry.toDate) {
								onsiteDate = this.$moment(entry.fromDate).format("DD/MM/YYYY") + " - " + this.$moment(entry.toDate).format("DD/MM/YYYY")
							} else if(entry.fromDate) {
								onsiteDate = this.$moment(entry.fromDate).format("DD/MM/YYYY")
							} else if(entry.toDate) {
								onsiteDate = this.$moment(entry.toDate).format("DD/MM/YYYY")
							}
							
							return {
								type: "completed",
								id: entry.id,
								sender: entry.recipientUsername || entry.supplierName,
								timestamp: this.$moment(entry.timestamp).format("DD/MM/YYYY HH:mm:ss"),
								templateName: entry.templateName,
								auditorName: entry.senderName,
								auditorType: entry.auditorType,
								onsiteDate: onsiteDate,
								visitDays: entry.visitDays ?? null,
								timestampRaw: entry.timestamp
							}
						})

					// Process assessed audits and filter by date
					this.assessedAudits = assessedResult.items
						.filter(entry => this.shouldShowAudit(entry.fromDate, entry.toDate))
						.map(entry => {
							let onsiteDate = null
							if(entry.fromDate && entry.toDate) {
								onsiteDate = this.$moment(entry.fromDate).format("DD/MM/YYYY") + " - " + this.$moment(entry.toDate).format("DD/MM/YYYY")
							} else if(entry.fromDate) {
								onsiteDate = this.$moment(entry.fromDate).format("DD/MM/YYYY")
							} else if(entry.toDate) {
								onsiteDate = this.$moment(entry.toDate).format("DD/MM/YYYY")
							}

							return {
								type: "assessed",
								id: entry.id,
								sender: entry.recipientUsername || entry.supplierName,
								timestamp: this.$moment(entry.timestamp).format("DD/MM/YYYY HH:mm:ss"),
								expiryDate: entry.expiryDate,
								templateName: entry.templateName,
								auditorName: entry.senderName,
								auditorType: entry.auditorType,
								onsiteDate: onsiteDate,
								visitDays: entry.visitDays ?? null,
								timestampRaw: entry.timestamp
							}
						})
					
					this.allAuditsToAssess = [
						...this.completedAudits,
						...this.assessedAudits
					].sort((a, b) => new Date(b.timestampRaw) - new Date(a.timestampRaw))

					this.filteredAuditsToAssess = [...this.allAuditsToAssess]
					this.loadImprovementsForAllAudits()
					this.loadingAssess = false
				})
				.catch(err => {
					console.error("Error loading audits to assess:", err)
					this.loadingAssess = false
				})
		},
		loadImprovementsForAllAudits() {
			const auditIds = this.completedAudits.map(audit => audit.id)
			
			Promise.all(
				auditIds.map(auditId => 
					AuditAPI.getImprovements()
						.then(result => ({
							auditId,
							improvements: result.filter(entry => entry.AuditID === auditId)
						}))
						.catch(() => ({ auditId, improvements: [] }))
				)
			).then(results => {
				this.auditImprovements = {}
				let totalSent = 0
				let totalCompleted = 0

				results.forEach(({ auditId, improvements }) => {
					const nonconformities = improvements.filter(e => e.NCRorOFI === 0)
					const improvementItems = improvements.filter(e => e.NCRorOFI === 1)
					const allItems = [...nonconformities, ...improvementItems]

					this.auditImprovements[auditId] = {
						nonconformities,
						improvements: improvementItems
					}

					if (this.filteredAuditsToAssess.some(a => a.id === auditId)) {
						totalSent += allItems.length
						totalCompleted += allItems.filter(i => i.Answer && i.Answer.trim() !== "").length
					}
				})
				this.sentCount = totalSent
				this.completedCount = totalCompleted
			}).catch(err => {
				console.error("Error loading improvements for audits:", err)
			})
		},
		hasIncompleteImprovements(auditId) {
			const auditData = this.auditImprovements[auditId]
			if (!auditData) return false
			
			const { nonconformities = [], improvements = [] } = auditData
			
			if (nonconformities.length === 0 && improvements.length === 0) {
				return false
			}
			
			const allItems = [...nonconformities, ...improvements]
			const hasIncomplete = allItems.some(item => {
				return !item.Answer || item.Answer.trim() === ""
			})
			return hasIncomplete
		},
		showRequestDialog(item) {
			this.submitterName = item.sender || item.invitationCodeName
			this.previewTemplateID = item.templateID
			this.dialogRequestView = true
		},
		searchPendingRequests() {
			this.pendingtemplates_options.page = 1
			this.applyPendingFilters()
		},
		
		clearPendingSearch() {
			this.templateName = null
			this.auditorName = null
			this.pendingtemplates_options.page = 1
			this.filteredCombinedRequests = [...this.allCombinedRequests]
		},
		
		applyPendingFilters() {
			let filteredData = [...this.allCombinedRequests]
			if (this.templateName || this.auditorName) {
				if (this.templateName) {
					filteredData = filteredData.filter(item => 
						item.templateName && item.templateName.toLowerCase().includes(this.templateName.toLowerCase())
					)
				}
				
				if (this.auditorName) {
					filteredData = filteredData.filter(item => 
						item.auditorName && item.auditorName.toLowerCase().includes(this.auditorName.toLowerCase())
					)
				}
			}
			
			this.filteredCombinedRequests = filteredData
		},
		
		searchAuditsToAssess() {
			this.auditstoassess_options.page = 1
			this.applyAssessFilters()
		},
		
		clearAssessSearch() {
			this.assessTemplateName = null
			this.supplierName = null
			this.assessAuditorName = null
			this.auditstoassess_options.page = 1
			this.filteredAuditsToAssess = [...this.allAuditsToAssess]
		},
		
		applyAssessFilters() {
			let filteredData = [...this.allAuditsToAssess]
			
			if (this.assessTemplateName || this.supplierName || this.assessAuditorName) {
				if (this.assessTemplateName) {
					filteredData = filteredData.filter(item => 
						item.templateName && item.templateName.toLowerCase().includes(this.assessTemplateName.toLowerCase())
					)
				}
				
				if (this.supplierName) {
					filteredData = filteredData.filter(item => 
						item.sender && item.sender.toLowerCase().includes(this.supplierName.toLowerCase())
					)
				}
				
				if (this.assessAuditorName) {
					filteredData = filteredData.filter(item => 
						item.auditorName && item.auditorName.toLowerCase().includes(this.assessAuditorName.toLowerCase())
					)
				}
			}
			
			this.filteredAuditsToAssess = filteredData
		},

		handleWorkflowStepClick(step) {      // ← NEW: Handles click on workflow steps
			this.activeStep = step
			this.tutorialActive = true
			
			this.$nextTick(() => {
				const targetRef = this.tutorialSteps[step].targetRef
				if (targetRef && this.$refs[targetRef]) {
					const el = this.$refs[targetRef]
					const element = el.$el || el
					element.scrollIntoView({ 
						behavior: "smooth", 
						block: "center" 
					})
				}
			})
		},
		
		closeTutorial() {                    // ← NEW: Closes tutorial mode
			this.tutorialActive = false
			this.activeStep = null
		},

		goToNextStep() {
			const currentIndex = this.stepOrder.indexOf(this.activeStep)

			if (currentIndex === -1 || this.isLastStep) {
				this.closeTutorial()
				return
			}

			const nextStep = this.stepOrder[currentIndex + 1]
			this.handleWorkflowStepClick(nextStep)
		}
	},
}
</script>
<style scoped>
.steps-wrapper {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	position: relative;
	z-index: 2;
	padding: 0 5%;
	border: 2px solid #1e7c83;
	border-radius: 5px;
}

.step-container {
  text-align: center;
  max-width: 100px;
}

.step-circle {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px auto;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.step-circle:hover {
  transform: translateY(-2px);
}

.step-text h5 {
  font-weight: 400;
  margin: 2px 0;
  line-height: 1.2;
}

.arrow-col {
	margin: 4% 0 0 3%;
}

.tutorial-highlight {
  position: relative;
  z-index: 10000 !important;
  border-radius: 8px;
}

.tutorial-grayed {
  opacity: 0.4;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.step-active {
  transform: scale(1.05);
}

.clickable-step {
	cursor: pointer;
}

.clickable-step .step-circle {
	cursor: pointer;
}

.clickable-step .v-icon {
	cursor: pointer;
}

@media (max-width: 768px) {
	.steps-wrapper {
		flex-direction: column;
		align-items: center;
		gap: 10px;
		padding: 15px;
	}
	
	.arrow-col {
		margin: 0;
		padding: 10px 0;
		width: auto;
		min-height: auto;
	}
	
	.arrow-col .v-icon {
		transform: rotate(90deg);
	}
	
	.step-text h5 {
		font-size: 1rem;
		min-height: auto;
	}
	
	.step-container {
		max-width: 200px;
	}
}

@media (max-width: 1024px) and (min-width: 769px) {
	.step-text h5 {
		font-size: 0.8rem;
	}
	
	.step-circle {
		width: 70px;
		height: 70px;
	}
	
	.step-circle .v-icon {
		font-size: 30px !important;
	}
	
	.steps-wrapper {
		padding: 15px;
	}
}

@media (min-width: 1025px) {
	.steps-wrapper {
		padding: 10px 15px;
	}
	
	.arrow-col {
		padding: 0 4px;
	}
}
</style>